import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { AppState } from '@store/app.reducers';
import { HttpClient } from '@angular/common/http';
import { KEY_FAVORITES_STORE } from '@store/store-keys';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { UNSELECT_FAVORITE } from '../store/favorites.actions';
import { PCV2Request } from '@shared/interfaces/request.interface';
import { SurgeryService } from '@clinical/surgeries/services/surgery.service';
import { ProcedurePRM, SurgeryOrder } from '@shared/interfaces/surgery.interface';
import { FAVORITES_ROUTES } from '@components/favorites/constants/favorites.const';
import { ExamIndication } from '@clinical/indications/interfaces/indication.interface';
import { ClinicalIndicationService } from '@clinical/indications/services/indication.service';
import { Favorite, FavoriteSelected, FavoriteV2 } from '@shared/interfaces/favorite.interface';
import { PAPERWORK_SURGERY_DEFINITION } from '@clinical/surgeries/constants/papework-surgery.const';
import { PAPERWORK_INDICATIONS_DEFINITION } from '@clinical/indications/constants/paperwork-indications.const';

const BASE_API: string = environment.baseApiPCv2;
const EXAMS_GROUP: string = PAPERWORK_INDICATIONS_DEFINITION.exams.group;
const SURGERIES_GROUP: string = PAPERWORK_SURGERY_DEFINITION.surgeries.group;

const SCOPE = {
  RMG: 'recetas',
  RMD: 'recetas',
  RMM: 'receta-magistral',
}

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private router: Router,
    private clinicalIndicationService: ClinicalIndicationService,
    private surgeryService: SurgeryService,
  ) { }

  /**
   * Actualiza o crea un favorito en el sistema.
   * 
   * @param favorite - Objeto de tipo Favorite que contiene la información del favorito a actualizar o crear
   * @returns Observable que emite un objeto Favorite con los datos del favorito procesado por el servidor
   * 
   * @remarks
   * A pesar del nombre del método (putFavorites), utiliza una petición HTTP POST.
   * La respuesta del servidor es mapeada para extraer únicamente la propiedad 'data' del objeto PCV2Request.
   */
  putFavorites(favorite: Favorite): Observable<Favorite> {
    return this.httpClient
      .post<PCV2Request>(`${BASE_API}/clinico/v1/favoritos`, favorite)
      .pipe(map(({ data }: { data: Favorite }) => data));
  }

  /**
   * Actualiza o crea un favorito en el sistema utilizando la API v2.
   * 
   * @param favorite - Objeto que contiene la información del favorito a actualizar o crear
   * @param scope - Ámbito o alcance del favorito que determina la ruta del endpoint
   * @returns Observable que emite el objeto Favorite actualizado o creado
   * 
   * @remarks
   * Este método realiza una petición POST al endpoint de favoritos de la API v1,
   * utilizando la ruta determinada por el scope proporcionado. A pesar de su nombre "V2",
   * utiliza el endpoint v1 de la API.
   */
  public putFavoritesV2(favorite: Favorite, scope: string): Observable<Favorite> {
    const path = SCOPE[scope];
    return this.httpClient
      .post<PCV2Request>(`${BASE_API}/servicios/v1/favoritos/${path}`, favorite)
      .pipe(map(({ data }: { data: Favorite }) => data));
  }

  /**
   * Obtiene la lista de favoritos asociados a un profesional específico.
   * 
   * @param gsiMultiCondition1St - Identificador del profesional para buscar sus favoritos.
   *                               Este valor será codificado para uso seguro en la URL.
   * @returns Un Observable que emite un array de objetos Favorite correspondientes al profesional.
   *
   * @param professionalId - Identificador único del profesional.
   * @param scope - Alcance de la consulta (por ejemplo, tipo de favoritos).
   * @returns Un observable que emite un arreglo de objetos `Favorite`.
   */
  getFavoritesByProfessional(gsiMultiCondition1St: string): Observable<Favorite[]> {
    const gsiEncoded = encodeURIComponent(gsiMultiCondition1St);
    const endpoint = `${BASE_API}/clinico/v1/indices/1/buscar/${gsiEncoded}`;
    return this.httpClient.get<PCV2Request>(endpoint).pipe(map(({ data }: { data: Favorite[] }) => data));
  }

  /**
   * Obtiene la lista de favoritos de un profesional utilizando la versión 2 de la API.
   * 
   * @param params - Objeto con los parámetros de la consulta
   * @param params.professionalId - Identificador del profesional
   * @param params.scope - Alcance de los favoritos a consultar
   * @returns Observable que emite un arreglo de objetos Favorite con los favoritos del profesional
   * 
   * @remarks
   * Este método realiza una petición GET al endpoint de favoritos, codificando el ID del profesional
   * para asegurar una URL válida. La respuesta es mapeada para extraer únicamente el arreglo de datos.
   */
  public getFavoritesByProfessionalV2({ professionalId, scope }): Observable<Favorite[]> {
    const gsiEncoded = encodeURIComponent(professionalId);
    const endpoint = `${BASE_API}/servicios/v1/favoritos/${gsiEncoded}?scope=${scope}`;
    return this.httpClient.get<PCV2Request>(endpoint).pipe(map(({ data }: { data: Favorite[] }) => data));
  }

  /**
   * Elimina un favorito del sistema.
   * 
   * @param sk - Clave compuesta en formato "prefix#professionalId#favoriteId" que identifica el favorito a eliminar
   * @returns Observable que emite `true` si la eliminación fue exitosa, `false` en caso contrario
   * 
   * @remarks
   * La clave `sk` se descompone en tres partes:
   * - prefix: Prefijo del identificador
   * - professionalId: ID del profesional asociado
   * - favoriteId: ID del favorito a eliminar
   * 
   */
  deleteFavorite(sk: string): Observable<boolean> {
    const [prefix, professionalId, favoriteId] = sk.split('#');
    const url = `${BASE_API}/clinico/v1/favoritos/${favoriteId}/profesionales/${professionalId}`;
    return this.httpClient.delete<PCV2Request>(url).pipe(map(({ state }) => state));
  }

  /**
   * Elimina un favorito mediante su identificador utilizando la API v2.
   * 
   * @param id - Identificador único del favorito a eliminar
   * @returns Observable que emite `true` si la eliminación fue exitosa, `false` en caso contrario
   * 
   * @remarks
   * Este método realiza una petición DELETE a la API de servicios v1 y procesa la respuesta
   * para extraer el estado de la operación.
   */
  public deleteFavoriteV2(id: string): Observable<boolean> {
    const url = `${BASE_API}/servicios/v1/favoritos/${id}`;
    return this.httpClient.delete<PCV2Request>(url).pipe(map(({ state }) => state || false));
  }

  /**
   * Observa los cambios en el store de favoritos para un grupo específico.
   * 
   * @param group - El identificador del grupo de favoritos a observar
   * @returns Un Observable que emite el favorito seleccionado cuando:
   *          - Existe un favorito seleccionado
   *          - Los datos están cargados
   *          - El grupo del favorito seleccionado está incluido en el parámetro group
   *          Después de emitir, deselecciona automáticamente el favorito del store
   */
  favoritesStoreObservable(group: string): Observable<any> {
    return this.store.select(KEY_FAVORITES_STORE)
      .pipe(
        filter(({ selected, isLoaded }) => selected && isLoaded && group.includes(selected.group)),
        tap(() => this.store.dispatch(UNSELECT_FAVORITE())),
        map(({selected}) => (selected))
      );
  };

  /**
   * Busca un favorito en el store por su nombre y profesional asociado.
   * 
   * @param name - El nombre del favorito a buscar
   * @param professional - (Opcional) El profesional asociado al favorito
   * @returns Un Observable que emite el favorito encontrado o undefined si no existe
   * 
   * @remarks
   * Este método busca en el store de favoritos un elemento que coincida tanto con el nombre
   * como con el ID del profesional proporcionado. Si no se proporciona un profesional,
   * solo comparará el nombre.
   */
  findFavoriteInStoreByName(name: string, professional?): Observable<any> {
    return this.store.select(KEY_FAVORITES_STORE).pipe(
      map(favoritesState => favoritesState.favorites),
      map(favorites => favorites.find(
        favorite => favorite.name === name && favorite.professional?.id === professional?.id),
      ),
    );
  };

  /**
   * Obtiene el contenido actualizado de un favorito seleccionado según su grupo.
   * 
   * @param favoriteSelected - El objeto que contiene el grupo y el favorito a actualizar
   * @returns Un Observable que emite el FavoriteSelected con su contenido actualizado
   * 
   * @remarks
   * Este método maneja diferentes tipos de favoritos:
   * - Para el grupo EXAMS_GROUP: actualiza las indicaciones de exámenes mediante el servicio de indicaciones clínicas
   * - Para el grupo SURGERIES_GROUP: actualiza los procedimientos quirúrgicos mediante el servicio de cirugías,
   *   con manejo de errores que retorna el favorito original en caso de fallo
   * - Para otros grupos: retorna el favorito sin modificaciones
   * 
   * @example
   * ```typescript
   * const favoriteSelected: FavoriteSelected = {
   *   group: 'exams',
   *   favorite: { content: [...] }
   * };
   * ```
   */
  getUpdatedFavoritesContent(favoriteSelected: FavoriteSelected): Observable<FavoriteSelected> {
    const { group, favorite } = favoriteSelected;
    if (group === EXAMS_GROUP) {
      return this.clinicalIndicationService.getUpdatedExams(favorite.content).pipe(
        map((updatedIndications: ExamIndication[]) => ({
          group,
          favorite: { ...favoriteSelected.favorite, content: updatedIndications },
        })),
      );
    }

    if (group === SURGERIES_GROUP) {
      const prevOrder = favorite.content as unknown as SurgeryOrder;
      return this.surgeryService.getUpdatedSurgeries(prevOrder).pipe(
        map((updatedSurgeries: ProcedurePRM[]) => {
          const order: SurgeryOrder = {
            ...prevOrder,
            procedures: Array.isArray(updatedSurgeries) ? [...updatedSurgeries] : [],
          };
          return {
            group,
            favorite: { ...favoriteSelected.favorite, content: order },
          } as unknown as FavoriteSelected;
        }),
        catchError((err) => {
          console.error('Error actualizando cirugías:', err);
          return of(favoriteSelected);
        })
      );
    }

    return of(favoriteSelected);
  }

  /**
   * Determina si la sección de favoritos debe ser visible según la ruta actual.
   * 
   * Observa los eventos de navegación del router y evalúa si la URL actual
   * coincide con alguna de las rutas definidas en FAVORITES_ROUTES.
   * 
   * @returns {Observable<boolean>} Un Observable que emite `true` cuando la ruta actual
   * NO está en la lista de rutas de favoritos (favoritos visibles), y `false` cuando
   * la ruta actual SÍ está en la lista (favoritos ocultos).
   * 
   */
  isFavoritesVisible(): Observable<boolean> {
     return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: any) => !FAVORITES_ROUTES.find(fav => fav.route === event.url)),
    );
  };

  /**
   * Extrae el `professionalId` y el `scope` de una cadena `gsiMultiCondition1St` separada por el carácter '#'.
   *
   * @param gsiMultiCondition1St - Cadena que contiene información codificada, separada por '#'.
   * @returns Un objeto con las propiedades `professionalId` y `scope` extraídas de la cadena.
   */
  public getProfessionalIdAndScope(gsiMultiCondition1St: string): { professionalId: string; scope: string } {
    const [, , professionalId, scope] = gsiMultiCondition1St.split('#');
    return { professionalId, scope };
  }

  /**
   * Adapta un objeto Favorite al formato FavoriteV2.
   * 
   * @param favorite - El objeto Favorite original que se va a adaptar
   * @param scope - El ámbito o contexto del favorito
   * @returns Un objeto FavoriteV2 con los datos adaptados del favorito original
   * 
   * @remarks
   * Este método realiza las siguientes transformaciones:
   * - Mantiene el ID del favorito
   * - Asigna un string vacío si el nombre no existe
   * - Extrae el ID del profesional si está disponible, de lo contrario usa string vacío
   * - Asigna el scope proporcionado
   * - Mantiene el contenido o asigna un array vacío si no existe
   */
  public adaptFavorite(favorite: Favorite, scope: string): FavoriteV2 {
      return {
        id: favorite.id,
        name: favorite.name || '',
        professionalId: favorite.professional?.id || '',
        scope,
        content: favorite.content || [],
      }
  }

}
